import { Search } from "@outschool/business-rules";

/**
 * Handles class size selection logic with logic to prevent non-contiguous selections
 * (e.g., allows 1-on-1 + small group, or small group + larger group, but not 1-on-1 + larger group)
 *
 * Generic function that works with any string type and ordered array of options.
 *
 * @param currentSelected - Currently selected items
 * @param newSelection - New selection (either from array changes or individual toggle)
 * @param allOptions - Ordered array of all possible options
 * @returns Final selected items after applying contiguous selection logic
 */
export function ensureContiguousClassSizeSelection<T extends string>(
  currentSelected: T[],
  newSelection: T[],
  allOptions: readonly T[],
): T[] {
  const wasAllSelected = currentSelected.length === allOptions.length;
  const isNowAllSelected = newSelection.length === allOptions.length;

  // If all options were selected and user unselected one, select only the unselected one
  if (wasAllSelected && !isNowAllSelected) {
    const unselectedOption = allOptions.find(
      option => !newSelection.includes(option),
    );
    if (unselectedOption) {
      return [unselectedOption];
    }
  }

  // Only allow selecting contiguous blocks of items
  const selectedIndices = newSelection
    .map(size => allOptions.indexOf(size))
    .filter(index => index !== -1)
    .sort((a, b) => a - b);

  if (selectedIndices.length > 1) {
    const isContiguous = selectedIndices.every(
      (index, i) => i === 0 || index === selectedIndices[i - 1] + 1,
    );

    if (!isContiguous) {
      // If non-contiguous selection, switch to just the newly selected option
      const newlySelected = newSelection.find(
        item => !currentSelected.includes(item),
      );
      if (newlySelected) {
        return [newlySelected];
      }
    }
  }

  return newSelection;
}

/**
 * Convenience wrapper for Search.ClassSize type with pre-configured options array
 */
export function ensureContiguousSearchClassSizeSelection(
  currentSelected: Search.ClassSize[],
  newSelection: Search.ClassSize[],
): Search.ClassSize[] {
  return ensureContiguousClassSizeSelection(
    currentSelected,
    newSelection,
    Search.ClassSizes,
  );
}
